import type { FC } from 'react';
import React, { useCallback, useMemo } from 'react';
import { useAuthentication } from '@pflegenavi/frontend/authentication';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { CircularProgress, Stack } from '@mui/material';
import type { ISignupFormValues } from '../components/signup/SignupForm';
import { SignupForm } from '../components/signup/SignupForm';
import { useErrorSnackbar } from '@pflegenavi/web-components';
import {
  useFamilyMemberProfile,
  useGetGetFamilyMemberCountries,
} from '@pflegenavi/frontend/api-nursing-home';
import { useInitializeFM } from '../hooks/useInitializeFM';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface SignupPageProps {}

export const SignupPage: FC<SignupPageProps> = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { enqueueErrorSnackbar } = useErrorSnackbar();
  const { t } = useTranslation();

  const { user } = useAuthentication();

  const { data: userProfile, isPending: isUserProfileLoading } =
    useFamilyMemberProfile({});

  const { selectedResident } = useInitializeFM();

  const { data: countries, isPending: isCountriesLoading } =
    useGetGetFamilyMemberCountries({ tenant: selectedResident?.tenant });

  const userId = user?.userId;

  const userDetails = useMemo(() => {
    return {
      firstName:
        userProfile?.user.firstName &&
        userProfile.user.firstName !== userProfile.user.eMail
          ? userProfile.user.firstName
          : '',
      lastName: userProfile?.user.lastName ?? '',
    };
  }, [userProfile]);

  const handleSignup = useCallback(
    async (values: ISignupFormValues) => {
      if (!userId) {
        throw new Error('Id of logged in user could not be obtained');
      }
      try {
        await new Promise((resolve) =>
          setTimeout(() => {
            // TODO: implement API call
            // eslint-disable-next-line no-console
            console.log(values);
            resolve(null);
          }, 1000)
        );

        enqueueSnackbar(t('service-provider-app.connect-account.success'), {
          variant: 'success',
        });
      } catch (e) {
        enqueueErrorSnackbar(
          e,
          t('service-provider-app.connect-account.error')
        );
      }
    },
    [enqueueErrorSnackbar, enqueueSnackbar, t, userId]
  );

  const isUserLoading = !userProfile || isUserProfileLoading;
  const isResidentLoading = !selectedResident;
  const isCountryLoading = !countries || isCountriesLoading;

  if (isUserLoading || isResidentLoading || isCountryLoading) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          height: '100%',
          overflow: 'hidden',
          width: '100%',
          display: 'flex',
        }}
      >
        <CircularProgress color="primary" variant="indeterminate" />
      </Stack>
    );
  }

  return (
    <SignupForm
      userDetails={userDetails}
      countries={countries.data}
      loading={isUserProfileLoading}
      handleSignup={handleSignup}
    />
  );
};
